import { nanoid } from 'nanoid';
import * as v from 'valibot';
import {
  AIAssistantClientSkillSchema,
  type AIAssistantClientSkill,
  type AIAssistantClientSkillInput,
  type ViewModelId,
} from 'balus-common';
import { createViewModelAIAssistantSkill, updateViewModelAIAssistantSkill } from './ViewModelAIAssistantSkillSource';

const MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS = 20;

export type PreparedViewModelAIAssistantSkillChange =
  | { operation: 'create'; before: null; skill: AIAssistantClientSkill }
  | {
      operation: 'update';
      before: AIAssistantClientSkill;
      skill: AIAssistantClientSkill;
    };

export type ViewModelAIAssistantSkillChangeErrorCode =
  'invalidSkill' | 'skillLimit' | 'skillNotFound' | 'duplicateName' | 'conflict';

type PrepareResult =
  | { type: 'ok'; prepared: PreparedViewModelAIAssistantSkillChange }
  | { type: 'error'; code: ViewModelAIAssistantSkillChangeErrorCode };

type CommitResult =
  | {
      type: 'ok';
      skills: AIAssistantClientSkill[];
      skill: AIAssistantClientSkill;
    }
  | { type: 'error'; code: ViewModelAIAssistantSkillChangeErrorCode };

export function prepareViewModelAIAssistantSkillCreate(
  input: AIAssistantClientSkillInput,
  currentSkills: AIAssistantClientSkill[],
  options?: { generateSkillId?: () => string }
): PrepareResult {
  if (currentSkills.length >= MAX_VIEW_MODEL_AI_ASSISTANT_SKILLS) {
    return { type: 'error', code: 'skillLimit' };
  }

  const skill = {
    id: options?.generateSkillId?.() ?? nanoid(),
    enabled: input.enabled ?? true,
    name: input.name,
    description: input.description,
    triggers: input.triggers,
    whenToUse: input.whenToUse,
    argumentHint: input.argumentHint,
    prompt: input.prompt,
  };
  const parsed = v.safeParse(AIAssistantClientSkillSchema, skill);
  if (!parsed.success) return { type: 'error', code: 'invalidSkill' };

  if (currentSkills.some((currentSkill) => currentSkill.name === parsed.output.name)) {
    return { type: 'error', code: 'duplicateName' };
  }

  return { type: 'ok', prepared: { operation: 'create', before: null, skill: parsed.output } };
}

export function prepareViewModelAIAssistantSkillUpdate(
  expectedSkill: AIAssistantClientSkill,
  input: AIAssistantClientSkillInput,
  currentSkills: AIAssistantClientSkill[]
): PrepareResult {
  const currentSkill = currentSkills.find((skill) => skill.id === expectedSkill.id) ?? null;
  if (currentSkill === null) return { type: 'error', code: 'skillNotFound' };

  const skill = {
    id: expectedSkill.id,
    enabled: input.enabled ?? expectedSkill.enabled,
    name: input.name,
    description: input.description,
    triggers: input.triggers,
    whenToUse: input.whenToUse,
    argumentHint: input.argumentHint,
    prompt: input.prompt,
  };
  const parsed = v.safeParse(AIAssistantClientSkillSchema, skill);
  if (!parsed.success) return { type: 'error', code: 'invalidSkill' };

  const duplicateName = currentSkills.some(
    (currentSkill) => currentSkill.id !== parsed.output.id && currentSkill.name === parsed.output.name
  );
  if (duplicateName && expectedSkill.name !== parsed.output.name) {
    return { type: 'error', code: 'duplicateName' };
  }
  return { type: 'ok', prepared: { operation: 'update', before: expectedSkill, skill: parsed.output } };
}

/**
 * 準備済みのSkill変更を永続化する。
 *
 * @throws ローカルSkillの移行または永続化に失敗した場合。呼び出し元で捕捉して保存失敗として扱うこと。
 */
export async function commitViewModelAIAssistantSkillChange(
  viewModelId: ViewModelId,
  prepared: PreparedViewModelAIAssistantSkillChange
): Promise<CommitResult> {
  const result =
    prepared.operation === 'create'
      ? await createViewModelAIAssistantSkill(viewModelId, prepared.skill)
      : await updateViewModelAIAssistantSkill(viewModelId, prepared.before, prepared.skill);
  if (result.type === 'error') return result;
  return { type: 'ok', skills: result.skills, skill: prepared.skill };
}
